import React, { useContext, useEffect, useState } from 'react'

//interfaces
import { ITableColumn, ITableContent, ITableRows } from  '@/interfaces/simpleTable';
import { AllUsersSortByType, ResourcePermission } from  '@/interfaces/orgSettings';
import { SortBy } from  '@/interfaces/toggle';

//hooks 
import { Truncate } from  '@/helpers/Text';
import { getStatus, isExternallyManaged } from  '@/helpers/OrgSettings';

//components
import UserIconAndName from  '@/components/elements/orgSettings/tableCellItems/UserIconAndName';
import UserRoleGroups from  '@/components/elements/orgSettings/tableCellItems/buttons/userRoleGroups';

//context
import { OrgSettingsUsersContext } from '@/context/OrgSettingsUsersContext';

export const allUsersScreenConstants = {
    userListDownloadLink: "/admin/OrganizationUser/ExportUsersReport",
    activeUserDownloadLink:  "/admin/OrganizationUser/ExportActiveUsersReport",
    userPublishingDownloadLink: "/admin/OrganizationUser/ExportUserGeneratedContentReport",
    userRecognitionDownloadLink: "/admin/OrganizationUser/ExportRecognitionReport",
    header: "All Users",
    addNewUserButtonText: "Add New User",
    addNewUserButtonLabel: "Open Modal for adding new user to your organisation",
    filterButtonLabel:"click to view filter options",
    searchInputLabel:  "Filter users with search input",
    panelTitle: "Include",
    archivedUserLabel: "Archived Users",
    noDataMessage: "No user data found.",
    userListButtonText: 'Export User List',
    userListButtonLabel: 'Download document with all users details',
    activeReportButtonText: 'Active Users Report',
    activeReportButtonLabel: 'Download document with all active user details',
    publishingButtonText: 'User Publishing Report',
    publishingButtonLabel: 'Download document with all users publishing details',
    recognitionButtonText: 'User Recognition Report',
    recognitionButtonLabel: 'Download document with all user recognition details'
}

const UseAllUsersScreen = () => {  
    const {notificationDetails, getUsers, userData, paginationSettings, searchString, setSearchString, setSortToggle, sortToggle, includeArchivedUsers, setIncludeArchivedUsers, isLoading, setIsLoading} = useContext(OrgSettingsUsersContext);
    const [showFilterPanel, setShowFilterPanel] = useState<boolean>(false);
    const [tableContent, setTableContent] = useState<ITableContent>();
    const [showSimpleTable, setShowSimpleTable] = useState<boolean>(false);
    const filterButton = includeArchivedUsers ? "Filter (1)" : "Filter";
    
    const columns: Array<ITableColumn | undefined> = [
        { title: "Username", isToggleButton: true, onSortAscendingClick: () => toggleSort(AllUsersSortByType.USERNAME, SortBy.DESCENDING), onSortDescendingClick: () => toggleSort(AllUsersSortByType.USERNAME, SortBy.ASCENDING)}, 
        { title: "First Name", isToggleButton: true, onSortAscendingClick: () => toggleSort(AllUsersSortByType.FIRST_NAME, SortBy.DESCENDING),  onSortDescendingClick: () => toggleSort(AllUsersSortByType.FIRST_NAME, SortBy.ASCENDING)}, 
        { title: "Last Name", isToggleButton: true, onSortAscendingClick: () => toggleSort(AllUsersSortByType.LAST_NAME, SortBy.DESCENDING), onSortDescendingClick: () => toggleSort(AllUsersSortByType.LAST_NAME, SortBy.ASCENDING)}, 
        { title: "Job Role", isToggleButton: true, onSortAscendingClick: () => toggleSort(AllUsersSortByType.JOB_ROLE, SortBy.DESCENDING),  onSortDescendingClick: () => toggleSort(AllUsersSortByType.JOB_ROLE, SortBy.ASCENDING)}, 
        { title: "Org Role", isToggleButton: true, onSortAscendingClick: () => toggleSort(AllUsersSortByType.PERMISSIONS, SortBy.ASCENDING), onSortDescendingClick: () => toggleSort(AllUsersSortByType.PERMISSIONS, SortBy.DESCENDING)},
        includeArchivedUsers
            ? { title: "Status", isToggleButton: true, onSortAscendingClick: () => toggleSort(AllUsersSortByType.STATUS, SortBy.ASCENDING), onSortDescendingClick: () => toggleSort(AllUsersSortByType.STATUS, SortBy.DESCENDING) }
            : undefined
    ];



    useEffect(()=> {
        setIsLoading(true);
        getUsers();
    }, [searchString, sortToggle, includeArchivedUsers]);

    useEffect(() => {
        if (userData === undefined) {
            setIsLoading(true);
            setTableContent({rows: [], columns: columns as ITableColumn[]});
            setShowSimpleTable(true);
            return;
        }

        let rows: ITableRows[] = [];
        userData?.forEach((user) => {
            const row: ITableRows = {
                content: [
                    <UserIconAndName 
                        key={`${user.id}-icon-and-name`}  
                        profilePicture={user.profilePicUrl} 
                        userId={user.id} 
                        username={user.userName} 
                        truncate={18}
                        isManagedUser={isExternallyManaged(user.userManagementType)}
                        externalLoginIcon={user.provider?.iconUrl}
                    />,
                    <span key={`${user.id}-fn`} className='bolder-text'>{Truncate(user.firstName ?? "", 12)}</span>, 
                    <span key={`${user.id}-ln`} className='bolder-text'>{Truncate(user.lastName ?? "", 12)}</span>, 
                    Truncate(user.jobRole ?? "", 12), 
                    <UserRoleGroups key={`${user.id}-role`} id={user.id} userRole={user.permission === ResourcePermission.MANAGE  ? 'Admin' : 'Member'} />,
                    includeArchivedUsers && getStatus(user?.status)
                ], 
                isHighlightedRow:isExternallyManaged(user.userManagementType),
                controls:[
                    {content:"Edit", onClick:() => onEditClick(user.id)}    
                ]
            };
            rows.push(row);
        });
 
        setTableContent({rows: rows, columns: columns as ITableColumn[]});
        setIsLoading(false);
        setShowSimpleTable(true);
    }, [userData])

    function toggleSort(sortByType: AllUsersSortByType, sortBy: SortBy) {
        setSortToggle([sortByType, sortBy]);
    }

    function onIncludeArchivedUserClick(){ 
        setIncludeArchivedUsers(!includeArchivedUsers);
    }

    function onEditClick(userId:any) {
        window.location.replace(`/admin/OrganizationUser/EditProfile/${userId}`);
    }
    
    function onAddUserClick() {
        window.location.replace(`/admin/OrganizationUser/Create`);
    }

    function getNewPage(page: number) {
        getUsers(page);
    }

    return{
        filterButton,
        isLoading,
        setSearchString,
        searchString,
        onAddUserClick,
        showSimpleTable,
        tableContent,
        includeArchivedUsers,
        onIncludeArchivedUserClick,
        setShowFilterPanel,
        showFilterPanel,
        notificationDetails,
        paginationSettings,
        getNewPage
    }
}

export default UseAllUsersScreen